import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import ItemTabs from 'components/ItemTabs'
import Select from 'components/Inputs/Select'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import SoldItemList from 'components/Lists/SoldItemList'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText,
  Column
} from '../../styles'

export default function SoldItems ({
  data: soldItems,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const TAB_OPTIONS = [
    { label: 'Gráfico geral', value: 'chart' },
    { label: 'Lista de itens', value: 'list' }
  ]

  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const itemFilterOptions = Dashboard.ITEM_FILTERS
  const [loading, setLoading] = useState (true)
  const [selectedTab, setSelectedTab] = useState (TAB_OPTIONS[0].value)
  const [isMounted, setMounted] = useState (false)
  const [showDetails, setShowDetails] = useState (false)
  const [soldItemsByMonth, setSoldItemsByMonth] = useState (null)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [itemFilter, setItemFilter] = useState (Dashboard.ITEM_FILTERS[0])
  const [monthFilter, setMonthFilter] = useState ({
    label: formattedDate,
    value: formattedDate
  })

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) setPeriodFilter (periodFilterOptions[0])
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()

      const filters = `start_date=${startDate}&end_date=${endDate}`
        
      const { data: soldItemsByMonth } = await cutileiApi.get (
        `/businesses/${businessId}/sold_items?${filters}`, requestConfig
      )

      setSoldItemsByMonth (soldItemsByMonth)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  const bestSellers = (a, b) => b.count - a.count

  if (!soldItems || Object.keys (soldItems).length === 0) {
    return null
  }

  return (
    <InfoContainer>
      <SubTitle>Itens vendidos - {selectedDate.toFormat ('MMMM / yyyy')}</SubTitle>
      <PieChart data={soldItems} format='amount'/>
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Itens vendidos'
        onClose={toggleDetails}
      >
        <FilterContainer>
          <Select
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
            containerStyles={{width: 250}}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <Column>
            <ItemTabs
              tabs={TAB_OPTIONS}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              containerStyle={{paddingTop: 12, paddingBottom: 20}}
            />
            {selectedTab === 'chart' ? (
              <ColumnChart
                data={[
                  ['Itens', 'Serviços', 'Pacotes', 'Produtos'],
                  ...Object.keys (soldItemsByMonth).map (month => ([
                    month,
                    soldItemsByMonth[month].services.total,
                    soldItemsByMonth[month].bundles.total,
                    soldItemsByMonth[month].products.total
                  ]))
                ]}
                options={{
                  isStacked: false,
                  height: 400,
                  chartArea: {
                    top: 10, left: 90, width: '100%', height: '80%'
                  },
                  vAxis: {
                    title: 'Qtd. vendida',
                    titleTextStyle: { bold: true }
                  }
                }}
              />
            ) : (
              <Column>
                <FilterContainer style={{marginTop: -7}}>
                  <Select
                    placeholder='Período...'
                    value={monthFilter}
                    options={Object.keys (soldItemsByMonth).map (month => ({
                      label: month, value: month
                    }))}
                    onChange={(_, option) => setMonthFilter (option)}
                  />
                  <Select
                    placeholder='Item...'
                    value={itemFilter}
                    options={itemFilterOptions}
                    onChange={(_, option) => setItemFilter (option)}
                  />
                </FilterContainer>
                <SoldItemList
                  items={soldItemsByMonth[monthFilter.value][itemFilter.value].list.sort (bestSellers)}
                />
              </Column>
            )}
          </Column>
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
